import { AppState, AppAction, Household, DataScope, TemplateType } from '../types'; // ADDED: TemplateType

/**
 * 🏁 INITIAL STATE
 * Matches the consolidated AppState interface.
 * activeDataScope defaults to 'PERSONAL' to ensure privacy-first boot sequences.
 */
export const initialState: AppState = {
  currentUser: null,
  households: [],
  currentHouseholdId: typeof window !== 'undefined' ? localStorage.getItem('everyday-os-household-id') : null,
  // 🛡️ Hybrid Scoping State
  activeDataScope: (typeof window !== 'undefined' ? localStorage.getItem('everyday-os-active-scope') as DataScope : 'PERSONAL') || 'PERSONAL',
  tasks: [],
  userProfiles: [], // Added userProfiles to initial state
  calendarEvents: [],
  inventory: [],
  contacts: [],
  maintenanceTasks: [],
  notifications: [],
  theme: 'dark',
  dailySpark: null,
  isBooting: true,
  lastSyncTimestamp: Date.now(),
  intelligence: {
    sparks: []
  },
  auditLogs: [],
  dashboardLayout: null,
};

/**
 * 🧠 MASTER APP REDUCER
 * Orchestrates Hybrid Scoping: /users/{uid} vs /households/{hid}.
 */
export const appReducer = (state: AppState, action: AppAction): AppState => {
  switch (action.type) {
    // --- 👤 IDENTITY & AUTH ---
    case 'SET_USER':
      return {
        ...state,
        currentUser: action.payload
      };

    // --- 🛰️ HOUSEHOLD & TENANCY MANAGEMENT ---
    case 'SET_HOUSEHOLDS':
      return {
        ...state,
        households: action.payload
      };

    case 'SET_CURRENT_HOUSEHOLD':
      /**
       * 🛡️ HYBRID SCOPING STRATEGY
       * When switching context, we must evaluate the validity of the current scope.
       * If moving to null (Personal Mode), scope MUST be 'PERSONAL'.
       */
      const nextHouseholdId = action.payload;
      // If switching to a non-household context, force scope to PERSONAL
      const nextScope: DataScope = nextHouseholdId ? state.activeDataScope : 'PERSONAL';

      if (typeof window !== 'undefined') {
        if (nextHouseholdId) {
          localStorage.setItem('everyday-os-household-id', nextHouseholdId);
        } else {
          localStorage.removeItem('everyday-os-household-id');
          localStorage.setItem('everyday-os-active-scope', 'PERSONAL'); // Always set personal mode for no household
        }
        // Also persist the current data scope for the next session
        localStorage.setItem('everyday-os-active-scope', nextScope);
      }

      return {
        ...state,
        currentHouseholdId: nextHouseholdId,
        activeDataScope: nextScope,
        // 🧹 Privacy Flush: Prevents shared data from leaking into personal view
        // These will be re-populated by new listeners in App.tsx
        tasks: [],
        calendarEvents: [],
        inventory: [],
        contacts: [], // 🧹 Clear contacts on context switch
        maintenanceTasks: [],
        auditLogs: []
      };

    case 'ADD_HOUSEHOLD':
      // Prevent duplicates when adding a new household
      const householdExists = state.households.some(h => h.id === action.payload.id);
      return {
        ...state,
        households: householdExists
          ? state.households.map(h => h.id === action.payload.id ? action.payload : h) // Update existing
          : [...state.households, action.payload], // Add new
      };

    // --- 🛡️ SCOPE CONTROL ---
    case 'SET_DATA_SCOPE':
      /**
       * 🔒 Privacy Guard: Users cannot set scope to 'SHARED' or 'BOTH'
       * if currentHouseholdId is null.
       */
      const validatedScope = (state.currentHouseholdId && ['SHARED', 'BOTH'].includes(action.payload))
        ? action.payload
        : 'PERSONAL'; // Force 'PERSONAL' if no household or invalid scope for no household

      if (typeof window !== 'undefined') {
        localStorage.setItem('everyday-os-active-scope', validatedScope);
      }

      return {
        ...state,
        activeDataScope: validatedScope,
        // 🧹 Context Flush: Required to re-sync listeners for the new scope
        // These will be re-populated by new listeners in App.tsx
        tasks: [],
        calendarEvents: [],
        inventory: [],
        contacts: [], // 🧹 Clear contacts to allow fresh hybrid sync
        maintenanceTasks: [],
      };

    case 'UPDATE_USER':
      return {
        ...state,
        currentUser: state.currentUser
          ? { ...state.currentUser, ...action.payload }
          : null
      };


    // --- 🛠️ FEATURE ENGINE DATA ---
    case 'SET_TASKS':
      return {
        ...state,
        tasks: action.payload
      };

    case 'ADD_TASK':
      // Add the new task to the main tasks array
      const existingTask = state.tasks.find(task => task.id === action.payload.id);
      let updatedTasks = existingTask
          ? state.tasks.map(task => task.id === action.payload.id ? action.payload : task) // Update existing if ID matches
          : [...state.tasks, action.payload]; // Add if new

      let updatedInventory = state.inventory;
      let updatedMaintenanceTasks = state.maintenanceTasks;

      if (action.payload.templateType === TemplateType.GROCERY) {
        const existingInventoryItem = state.inventory.find(item => item.id === action.payload.id);
        updatedInventory = existingInventoryItem
          ? state.inventory.map(item => item.id === action.payload.id ? action.payload : item)
          : [...state.inventory, action.payload];
      } else if (action.payload.templateType === TemplateType.MAINTENANCE) {
        const existingMaintenanceItem = state.maintenanceTasks.find(maint => maint.id === action.payload.id);
        updatedMaintenanceTasks = existingMaintenanceItem
          ? state.maintenanceTasks.map(maint => maint.id === action.payload.id ? action.payload : maint)
          : [...state.maintenanceTasks, action.payload];
      }

      return {
        ...state,
        tasks: updatedTasks,
        inventory: updatedInventory,
        maintenanceTasks: updatedMaintenanceTasks,
      };

    case 'UPDATE_TASK':
      const newTasks = state.tasks.map(task =>
        task.id === action.payload.id ? { ...task, ...action.payload.updates } : task
      );
      const newInventory = state.inventory.map(item =>
        item.id === action.payload.id ? { ...item, ...action.payload.updates } : item
      );
      const newMaintenanceTasks = state.maintenanceTasks.map(maint =>
        maint.id === action.payload.id ? { ...maint, ...action.payload.updates } : maint
      );
      return {
        ...state,
        tasks: newTasks,
        inventory: newInventory,
        maintenanceTasks: newMaintenanceTasks,
      };

    case 'SET_CALENDAR_EVENTS':
      return {
        ...state,
        calendarEvents: action.payload
      };

    case 'SET_INVENTORY':
      return {
        ...state,
        inventory: action.payload
      };

    case 'SET_CONTACTS': // ✨ Action to handle hydrated hybrid contact list
      return {
        ...state,
        contacts: action.payload
      };

    case 'SET_MAINTENANCE_TASKS':
      return {
        ...state,
        maintenanceTasks: action.payload
      };

    case 'SET_NOTIFICATIONS':
      return {
        ...state,
        notifications: action.payload
      };

    // --- 📐 DASHBOARD & UI ---
    case 'UPDATE_LAYOUT':
      return {
        ...state,
        currentUser: state.currentUser
          ? { ...state.currentUser, dashboardLayout: action.payload }
          : null
      };

    case 'TOGGLE_THEME': {
      const newTheme = state.theme === 'light' ? 'dark' : 'light';
      return { ...state, theme: newTheme };
    }

    // --- 🛰️ SYSTEM & SYNC ---
    case 'SET_SYNC':
      return {
        ...state,
        lastSyncTimestamp: action.payload
      };

    case 'SET_BOOTING':
      return {
        ...state,
        isBooting: action.payload
      };

    default:
      return state;
  }
};